import { Box, Paper, Skeleton, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';

const styles = {
    summary: { display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2.5, mb: 4 },
    summaryCard: { p: { xs: 2, sm: 3 }, minHeight: 116, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 },
    charts: { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3, mb: 3 },
    chartCard: { p: 3, borderRadius: 3, minHeight: 312 },
    pieLayout: { mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 3 },
    pie: (theme) => ({
        flexShrink: 0,
        width: { xs: 132, md: 196 },
        height: { xs: 132, md: 196 },
        borderRadius: '50%',
        background: `conic-gradient(${alpha(theme.palette.orange?.main || '#f4b183', 0.6)} 0 37%, transparent 37% 37.5%, ${alpha(
            '#90b9ed',
            0.7
        )} 37.5% 71%, transparent 71% 71.5%, ${alpha(theme.palette.secondary.main, 0.5)} 71.5% 100%)`
    }),
    bars: { display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, alignItems: 'flex-end', height: 216, mt: 2, px: 2 },
    table: { p: 3, borderRadius: 3 },
    row: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 3, mt: 3 }
};

/**
 * Render decorative placeholders without mounting chart libraries or report queries.
 * @returns {JSX.Element} Static dashboard preview.
 */
export default function BackgroundSkeleton() {
    return (
        <Box aria-hidden="true" sx={{ height: '100%' }}>
            <Box sx={styles.summary}>
                {[0, 1, 2, 3].map((item) => (
                    <Paper key={item} elevation={0} sx={styles.summaryCard}>
                        <Skeleton animation={false} variant="circular" width={56} height={56} sx={{ flexShrink: 0 }} />
                        <Stack spacing={1.25} sx={{ width: '55%' }}>
                            <Skeleton animation={false} variant="rounded" width="75%" height={14} />
                            <Skeleton animation={false} variant="rounded" width="90%" height={11} />
                        </Stack>
                    </Paper>
                ))}
            </Box>
            <Box sx={styles.charts}>
                <Paper elevation={0} sx={styles.chartCard}>
                    <Skeleton animation={false} variant="rounded" width="35%" height={14} />
                    <Skeleton animation={false} variant="rounded" width="24%" height={11} sx={{ mt: 1.5 }} />
                    <Box sx={styles.bars}>
                        {Array(6)
                            .fill('bar-2')
                            .map((cell, k) => (
                                <Skeleton key={`${cell}+${k}`} animation={false} variant="rounded" width="100%" height={25} />
                            ))}
                    </Box>
                </Paper>
                <Paper elevation={0} sx={styles.chartCard}>
                    <Skeleton animation={false} variant="rounded" width="40%" height={14} />
                    <Skeleton animation={false} variant="rounded" width="24%" height={11} sx={{ mt: 1.5 }} />
                    <Box sx={styles.bars}>
                        {Array(6)
                            .fill('bar1-')
                            .map((cell, k) => (
                                <Skeleton key={`${cell}+${k}`} animation={false} variant="rounded" width="100%" height={25} />
                            ))}
                    </Box>
                </Paper>
            </Box>
            <Paper elevation={0} sx={styles.table}>
                <Skeleton animation={false} variant="rounded" width="25%" height={14} />
                {[0, 1, 2].map((row) => (
                    <Box key={row} sx={styles.row}>
                        {[0, 1, 2, 3].map((cell) => (
                            <Skeleton key={cell} animation={false} variant="rounded" width="75%" height={14} />
                        ))}
                    </Box>
                ))}
            </Paper>
            <Paper elevation={0} sx={styles.table}>
                <Skeleton animation={false} variant="rounded" width="25%" height={14} />
                {[0, 1, 2].map((row) => (
                    <Box key={row} sx={styles.row}>
                        {[0, 1, 2, 3].map((cell) => (
                            <Skeleton key={cell} animation={false} variant="rounded" width="75%" height={14} />
                        ))}
                    </Box>
                ))}
            </Paper>
            <Paper elevation={0} sx={styles.table}>
                <Skeleton animation={false} variant="rounded" width="25%" height={14} />
                {[0, 1, 2].map((row) => (
                    <Box key={row} sx={styles.row}>
                        {[0, 1, 2, 3].map((cell) => (
                            <Skeleton key={cell} animation={false} variant="rounded" width="75%" height={14} />
                        ))}
                    </Box>
                ))}
            </Paper>
            <Paper elevation={0} sx={styles.table}>
                <Skeleton animation={false} variant="rounded" width="25%" height={14} />
                {[0, 1, 2].map((row) => (
                    <Box key={row} sx={styles.row}>
                        {[0, 1, 2, 3].map((cell) => (
                            <Skeleton key={cell} animation={false} variant="rounded" width="75%" height={14} />
                        ))}
                    </Box>
                ))}
            </Paper>
        </Box>
    );
}
